import type { Dispatch, ReactElement, SetStateAction } from "react"
import { Fragment, useContext, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useMutation, useQuery } from "react-query"
import { useSelector } from "react-redux"
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels"
import { useParams } from "react-router-dom"
import { ReactFlowProvider } from "reactflow"

import { Close, DragIndicator } from "@mui/icons-material"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { CircularProgress, Grid, IconButton } from "@mui/material"
import { Button, InputText, Menu, MenuItem, NotificationUtils, Select, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { InfoContainer } from "../../../../components/core/InfoContainer"
import { BaseSimpleDialog } from "../../../../components/dialogs/BaseSimpleDialog"
import { VersioningDialog } from "../../../../components/dialogs/VersioningDialog"
import { VersionTag } from "../../../../components/status/VersionTag"
import { ConstantsHistory } from "../../../../components/tables/ConstantsHistory"
import { KonanEmptyState } from "../../../../components/utility/KonanEmptyState"
import { KonanAPI } from "../../../../services/KonanAPI"
import { queryClient } from "../../../../store/QueryClientStore"
import type { RootState } from "../../../../store/ReduxStore"
import type { UpdateWorkflowRequest } from "../../../../types/custom/projects"
import type { VersionChangeRequest } from "../../../../types/custom/rules"
import type { PaginatedWorkflowVersionListList } from "../../../../types/generated/api/PaginatedWorkflowVersionListList"
import { WorkflowGroupRetrieve } from "../../../../types/generated/api/WorkflowGroupRetrieve"
import type { WorkflowVersionList } from "../../../../types/generated/api/WorkflowVersionList"
import type { WorkflowVersionRetrieve } from "../../../../types/generated/api/WorkflowVersionRetrieve"
import { useIsPermitted } from "../../../../utils/PermissionsHelpers"
import { getValidMaxMinorVersion } from "../../../../utils/deploymentDetailsHelpers"
import { extractPageFromBackEndPaginationLink } from "../../../../utils/genericHelpers"
import { PredictionCreationFlow } from "../../../Predictions"
import { SimulationCreationDialog } from "../../../Simulations"
import { WorkflowCanvasContext } from "../../context/Context"
import type { ConfirmCloseDialogState } from "../../types/interfaces"
import { convertRetrievedNodesToFlow, getLayoutedElements } from "../../utils/workflowHelpers"
import { OutputFiltersDialog } from "../dialogs/OutputFiltersDialog"
import { WorkflowSchemaDialogContainer } from "../dialogs/WorkflowSchemaDialog/Container"
import { WorkflowCanvas } from "./WorkflowCanvas"

import styles from "../../Workflows.module.scss"

type WorkflowSwitch = {
  id: string
  name: string
  newState: string
}

type WorkflowControlsProps = {
  isWorkflowRefetching: boolean
  workflowsCount: number
  isCreateWorkflowMutationLoading: boolean
  isUpdateWorkflowMutationLoading: boolean
  handleEditClicked: () => void
  handleDuplicateWorkflow: () => void
  setConfirmCloseDialogOpen: Dispatch<SetStateAction<ConfirmCloseDialogState>>
}

/**
 * WorkflowControls component that provides controls and actions for workflow management.
 * Handles workflow state switching, versioning, schema management, and various workflow operations.
 */
export function WorkflowControls(props: Readonly<WorkflowControlsProps>): ReactElement {
  // Prop destructuring
  const {
    isWorkflowRefetching,
    workflowsCount,
    isCreateWorkflowMutationLoading,
    isUpdateWorkflowMutationLoading,
    handleEditClicked,
    handleDuplicateWorkflow,
    setConfirmCloseDialogOpen,
  } = props

  // Navigation hooks
  const { id: projectId } = useParams<{ id: string }>()

  // Context hooks
  const { mode, workflow, isWorkflowLoading, isWorkflowsLoading, isWorkflowFetching, formik } =
    useContext(WorkflowCanvasContext)

  // Redux hooks
  const currentProjectId = useSelector((state: RootState) => state.project.currentProject?.uuid)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)
  const permissions = useSelector((state: RootState) => state.auth.permissions)

  // Permission hooks
  const canAddWorkflowResponseFilter = useIsPermitted(
    "Add workflow response filter",
    permissions.konan,
    flattenedKonanPermissions,
  )

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "workflowControls",
  })

  // Constants
  const workflowId = workflow?.uuid as string
  const workflowName = workflow?.name
  const workflowState = workflow?.state
  const activeWorkflowVersion = workflow?.active_version

  // State hooks
  // Dialog state
  const [showConstants, setShowConstants] = useState<boolean>(false)
  const [switchWorkflowDialogOpen, setSwitchWorkflowDialogOpen] = useState<boolean>(false)
  const [versioningDialogOpen, setVersioningDialogOpen] = useState<boolean>(false)
  const [isSimulationDialogOpen, setIsSimulationDialogOpen] = useState<boolean>(false)
  const [isOutputFiltersOpen, setIsOutputFiltersOpen] = useState<boolean>(false)
  const [schemaDialogOpen, setSchemaDialogOpen] = useState<boolean>(false)
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false)

  // Workflow state
  const [workflowToSwitch, setWorkflowToSwitch] = useState<WorkflowSwitch>({ name: "", id: "", newState: "" })
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(activeWorkflowVersion?.version)
  const [nodes, setNodes] = useState<Node[]>()
  const [cachedResults, setCachedResults] = useState<Record<string, string>>({})

  // UI state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isTestModal, setIsTestModal] = useState(false)

  // Computed values
  const isMenuOpen = Boolean(anchorEl)

  // Mutations
  const switchWorkflowMutation = useMutation<AxiosResponse, AxiosError, UpdateWorkflowRequest>(KonanAPI.updateWorkflow)

  const changeWorkflowActiveVersionMutation = useMutation<AxiosResponse, AxiosError, VersionChangeRequest>(
    KonanAPI.changeResourceVersion,
    {
      mutationKey: "changeWorkflowVersion",
    },
  )

  // Data fetching queries
  const {
    isLoading: isVersionsListLoading,
    isFetchingNextPage,
    data: versionsList,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedWorkflowVersionListList>, AxiosError>(
    ["workflow-versions", projectId, workflowId],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchWorkflowVersions({
        projectId: projectId as string,
        workflowUUID: workflowId,
        pageSize: 20,
        page: pageParam,
      }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId && !!workflowId && versioningDialogOpen,
    },
  )

  const { isLoading: isWorkflowVersionLoading, data: workflowVersionData } = useQuery<
    AxiosResponse<WorkflowVersionRetrieve>,
    AxiosError
  >(
    ["workflow-version", projectId, selectedVersion],
    () => KonanAPI.fetchWorkflowVersion(projectId as string, selectedVersion?.toString() as string, workflowId),
    {
      enabled: !!projectId && !!selectedVersion && !!workflowId,
      onSuccess: () => {
        if (selectedVersion && !cachedResults[selectedVersion?.split(".")?.[0] as string]) {
          const result = getValidMaxMinorVersion(adjustedWorkflowVersions, selectedVersion)
          setCachedResults((prevResults) => ({
            ...prevResults,
            ...result,
          }))
        }
      },
    },
  )

  // Intersection observer hook
  const { ref: lastVersionRef } = useInView({
    onChange: (inView: boolean) => {
      inView && hasNextPage && !isVersionsListLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  // Memoized values
  const adjustedWorkflowVersions = useMemo(() => {
    return (
      versionsList?.pages.reduce(
        (accumulator, page) => accumulator.concat(page.data.results as WorkflowVersionList[]),
        [] as WorkflowVersionList[],
      ) ?? []
    )
  }, [versionsList?.pages])

  // Helper functions
  const handleSwitchWorkflow = (switchType: string): void => {
    setWorkflowToSwitch({
      id: workflowId,
      name: workflowName as string,
      newState: switchType,
    })
    setSwitchWorkflowDialogOpen(true)
  }

  const handleCancel = (): void => {
    setConfirmCloseDialogOpen({ isOpen: true, action: { name: mode === "create" ? "close" : "edit" } })
  }

  const switchWorkflow = async (): Promise<void> => {
    try {
      await switchWorkflowMutation.mutateAsync({
        newState: workflowToSwitch?.newState,
        projectId: currentProjectId as string,
        workflowUUID: workflowToSwitch?.id,
      })

      await queryClient.invalidateQueries("models")
      await queryClient.invalidateQueries("workflow")
      await queryClient.invalidateQueries(["workflows", currentProjectId])

      NotificationUtils.toast(
        t("notifications.switchWorkflow.success", {
          workflowName: workflowName as string,
          newState: workflowToSwitch?.newState?.toLowerCase(),
        }),
        {
          severity: "success",
        },
      )
      setSwitchWorkflowDialogOpen(false)
    } catch (e) {
      NotificationUtils.toast(t("notifications.switchWorkflow.failed", { workflowName: workflowName as string }), {
        severity: "error",
      })
    }
  }

  const handleRestoreVersion = async (version: string): Promise<void> => {
    try {
      await changeWorkflowActiveVersionMutation.mutateAsync({
        projectId: currentProjectId as string,
        resourceId: workflowId ?? "",
        version: version.toString(),
        resource: "workflows",
      })
      await queryClient.invalidateQueries("workflows")
      await queryClient.invalidateQueries(["workflow", currentProjectId, workflowId])
      await queryClient.invalidateQueries(["workflow-versions", currentProjectId, workflowId])

      NotificationUtils.toast(t("notifications.restoreVersion.success", { version: version }), {
        severity: "success",
      })
    } catch (error) {
      NotificationUtils.toast(t("notifications.restoreVersion.failed", { version: version }), {
        severity: "error",
      })
    }
  }

  const WorkflowDialogContainer = (): ReactElement => {
    // extracting the current major version
    const majorSelectedVersion = (selectedVersion ?? (activeWorkflowVersion?.version as string))?.split(".")?.[0]

    // show restore button when the current major version from the selected version/active version existed in our valid versions hashMap
    // and it's not the current active version
    const shouldShowRestoreButton =
      majorSelectedVersion &&
      cachedResults[majorSelectedVersion] === (selectedVersion ?? activeWorkflowVersion?.version) &&
      cachedResults[majorSelectedVersion] !== activeWorkflowVersion?.version

    /**
     * Updates nodes and layout when selected version changes.
     * Converts retrieved workflow data to flow format and applies layout.
     */
    useEffect(() => {
      if (selectedVersion && workflowVersionData?.data) {
        const { nodes, edges } = convertRetrievedNodesToFlow(
          workflowVersionData?.data?.nodes,
          workflowVersionData?.data?.start_node ?? "0",
          "retrieve",
          () => null,
          activeWorkflowVersion?.schema ?? { features: [], is_valid: false },
          formik,
        )
        setNodes([...nodes])
        getLayoutedElements([...nodes], [...edges])
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflowVersionData, selectedVersion])

    return (
      <Grid item container height="100%" direction={"column"} flexWrap={"nowrap"}>
        <Grid
          item
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          className={"versioning-dialog-header"}
        >
          <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
            <Grid item gap={1} xs={9} display="flex">
              <VersionTag
                version={selectedVersion ?? activeWorkflowVersion?.version}
                color={
                  activeWorkflowVersion?.is_deprecated
                    ? "red"
                    : selectedVersion
                      ? selectedVersion === activeWorkflowVersion?.version
                        ? "green"
                        : "blue"
                      : "green"
                }
              />
            </Grid>

            {shouldShowRestoreButton && (
              <Button
                variant="secondary"
                size="small"
                onClick={() => handleRestoreVersion(selectedVersion as string)}
                disabled={changeWorkflowActiveVersionMutation.isLoading}
                isLoading={changeWorkflowActiveVersionMutation.isLoading}
              >
                {t("versioningDialog.buttons.restore")}
              </Button>
            )}
          </Grid>
        </Grid>

        {isWorkflowVersionLoading || !selectedVersion || !workflowVersionData?.data ? (
          <Grid item xs={12} container className="dotted-background">
            <InfoContainer
              icon={<CircularProgress className={styles.circularProgress} />}
              title={t("loadingWorkflow")}
            />
          </Grid>
        ) : (
          <PanelGroup autoSaveId="workflow_history" direction="horizontal">
            <Panel id="workflow_canvas" maxSize={65} order={1}>
              <ReactFlowProvider>
                <Grid item container xs className={`versioning-dialog-container layoutflow`}>
                  <WorkflowCanvas
                    nodes={
                      nodes ??
                      convertRetrievedNodesToFlow(
                        workflowVersionData?.data?.nodes,
                        workflowVersionData?.data?.start_node ?? "0",
                        "retrieve",
                        () => null,
                        activeWorkflowVersion?.schema ?? { features: [], is_valid: false },
                        formik,
                      )?.nodes
                    }
                    edges={
                      convertRetrievedNodesToFlow(
                        workflowVersionData?.data?.nodes,
                        workflowVersionData?.data?.start_node ?? "0",
                        "retrieve",
                        () => null,
                        activeWorkflowVersion?.schema ?? { features: [], is_valid: false },
                        formik,
                      )?.edges
                    }
                    workflowControls={
                      !showConstants && (
                        <Button variant="secondary" onClick={() => setShowConstants((prev) => !prev)}>
                          {t("versioningDialog.buttons.viewConstants")}
                        </Button>
                      )
                    }
                  />
                </Grid>
              </ReactFlowProvider>
            </Panel>
            {showConstants && workflowVersionData?.data?.constants && (
              <Fragment>
                <PanelResizeHandle className={styles.resizeHandle}>
                  <DragIndicator className={styles.dragIcon} />
                </PanelResizeHandle>

                <Panel maxSize={70} id="constants_table" order={2} className={styles.constantsPanel}>
                  <Grid item container className={styles.constantsHistory}>
                    <Grid item container justifyContent={"space-between"}>
                      <Typography variant="h3-bold">{t("versioningDialog.constants")}</Typography>

                      <IconButton size="small" onClick={() => setShowConstants(false)}>
                        <Close fontSize="inherit" style={{ color: "var(--secondary-icon-default)" }} />
                      </IconButton>
                    </Grid>

                    {workflowVersionData?.data?.constants?.length <= 0 ? (
                      <Grid item xs alignContent={"center"}>
                        <KonanEmptyState title={t("versioningDialog.noConstantsFound")} />
                      </Grid>
                    ) : (
                      <ConstantsHistory
                        data={workflowVersionData?.data?.constants}
                        isLoading={isWorkflowVersionLoading}
                      />
                    )}
                  </Grid>
                </Panel>
              </Fragment>
            )}
          </PanelGroup>
        )}
      </Grid>
    )
  }

  return (
    <Fragment>
      {switchWorkflowDialogOpen && (
        <BaseSimpleDialog
          open={switchWorkflowDialogOpen}
          onClose={() => setSwitchWorkflowDialogOpen(false)}
          name={workflowToSwitch?.name}
          newState={workflowToSwitch?.newState}
          mode="workflow-switch"
          onAccept={() => switchWorkflow()}
          isLoading={switchWorkflowMutation.isLoading}
        />
      )}

      <WorkflowSchemaDialogContainer
        formik={formik}
        open={schemaDialogOpen}
        onClose={() => setSchemaDialogOpen(false)}
        schema={activeWorkflowVersion?.schema.features}
        workflowName={workflowName as string}
        isWorkflowsLoading={isWorkflowsLoading}
        isWorkflowInReadMode={mode !== "create" && mode !== "edit"}
        isWorkflowDeprecated={activeWorkflowVersion?.is_deprecated as boolean}
      />

      <PredictionCreationFlow
        isTestModal={isTestModal}
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
      />

      <VersioningDialog
        activeVersion={activeWorkflowVersion as WorkflowVersionRetrieve}
        setSelectedVersion={setSelectedVersion}
        selectedVersion={selectedVersion ?? activeWorkflowVersion?.version}
        isOpen={versioningDialogOpen}
        versions={adjustedWorkflowVersions ?? []}
        isSideBarDataLoading={isVersionsListLoading}
        children={WorkflowDialogContainer()}
        onClose={() =>
          setTimeout(() => {
            setVersioningDialogOpen(false)
            setSelectedVersion(activeWorkflowVersion?.version)
          }, 100)
        }
        infiniteScrollProps={{
          hasNextPage,
          isFetchingNextPage,
          lastVersionRef,
        }}
      />

      {isOutputFiltersOpen && (
        <OutputFiltersDialog
          isOpen={isOutputFiltersOpen}
          onClose={() => setIsOutputFiltersOpen(false)}
          uuid={workflowId}
          isWorkflowDeprecated={activeWorkflowVersion?.is_deprecated}
          workflow={activeWorkflowVersion as WorkflowVersionRetrieve}
        />
      )}

      {isSimulationDialogOpen && (
        <SimulationCreationDialog
          open={isSimulationDialogOpen}
          onClose={() => {
            setIsSimulationDialogOpen(false)
          }}
          workflowID={workflowId}
          workflowName={workflowName as string}
        />
      )}

      {/* Refactor to reduce duplication */}
      <Grid item alignItems="flex-start" justifyContent="end">
        {mode !== "create" && workflowsCount > 0 && mode !== "edit" && (
          <div className={styles.buttonsContainer}>
            {workflowState === WorkflowGroupRetrieve.state.ACTIVE && (
              <Button
                variant="primary"
                onClick={() => {
                  setIsTestModal(false)
                  setIsUploadDialogOpen(true)
                }}
                key={"upload_btn"}
              >
                {t("buttons.predict")}
              </Button>
            )}

            {!activeWorkflowVersion?.is_deprecated && (
              <Button
                variant="secondary"
                onClick={() => {
                  setIsTestModal(true)
                  setIsUploadDialogOpen(true)
                }}
              >
                {t("buttons.testWorkflow")}
              </Button>
            )}

            <Button
              variant="secondary"
              onClick={() => setSchemaDialogOpen(true)}
              disabled={isWorkflowLoading || isWorkflowsLoading}
            >
              {t("buttons.schema")}
            </Button>

            {/* Enable switch workflow state, if it's not deprecated */}
            {!activeWorkflowVersion?.is_deprecated && (
              <Select
                placeholder={t("buttons.switch.placeholder")}
                value={t("buttons.switch.placeholder")}
                disabled={isWorkflowLoading || isWorkflowsLoading}
                optionsWithActions={[
                  {
                    label: t("buttons.switch.activate"),
                    action: () => handleSwitchWorkflow("ACTIVE"),
                    disabled: workflowState === "ACTIVE",
                  },
                  {
                    label: t("buttons.switch.disable"),
                    action: () => handleSwitchWorkflow("DISABLED"),
                    disabled: workflowState === "DISABLED",
                  },
                ]}
                width={115}
              />
            )}

            {/* Deprecated workflows are read-mode only. cannot be simulated nor edited */}
            {!activeWorkflowVersion?.is_deprecated && (
              <Fragment>
                <Button
                  onClick={handleEditClicked}
                  variant="secondary"
                  disabled={
                    isWorkflowLoading ||
                    isWorkflowsLoading ||
                    isWorkflowFetching ||
                    isWorkflowRefetching ||
                    isUpdateWorkflowMutationLoading
                  }
                >
                  {t("buttons.edit")}
                </Button>

                <Button variant="secondary" onClick={(e) => setAnchorEl(e?.target as HTMLElement)}>
                  <MoreHorizIcon fontSize="small" />
                </Button>

                <Menu menuMaxContent anchorEl={anchorEl} open={isMenuOpen} onClose={() => setAnchorEl(null)}>
                  <MenuItem
                    onClick={() => {
                      handleDuplicateWorkflow()
                      setAnchorEl(null)
                    }}
                    disabled={isWorkflowLoading || isWorkflowsLoading}
                  >
                    {t("buttons.menu.duplicate")}
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      setVersioningDialogOpen(true)
                      setAnchorEl(null)
                    }}
                    disabled={isWorkflowLoading || isWorkflowsLoading}
                  >
                    {t("buttons.menu.history")}
                  </MenuItem>

                  {canAddWorkflowResponseFilter && (
                    <MenuItem
                      onClick={() => {
                        setIsOutputFiltersOpen(true)
                        setAnchorEl(null)
                      }}
                      disabled={isWorkflowLoading || isWorkflowsLoading}
                    >
                      {t("buttons.menu.outputFilters")}
                    </MenuItem>
                  )}

                  {!activeWorkflowVersion?.is_deprecated && (
                    <MenuItem
                      onClick={() => {
                        setIsSimulationDialogOpen(true)
                        setAnchorEl(null)
                      }}
                      disabled={isWorkflowLoading || isWorkflowsLoading}
                    >
                      {t("buttons.menu.simulate")}
                    </MenuItem>
                  )}
                </Menu>
              </Fragment>
            )}
          </div>
        )}
        {(mode === "create" || mode === "edit") && (
          <div className={styles.editButtonsContainer}>
            <Grid item className={styles.inputContainer}>
              <InputText
                hideDescription
                variant="filled"
                id="workflowName"
                placeholder={t("createEditMode.workflowNamePlaceholder")}
                value={formik.values.workflowName}
                key={workflow?.uuid}
                handleChange={formik.handleChange}
                error={formik.touched.workflowName && Boolean(formik.errors.workflowName) && formik.errors.workflowName}
                handleBlur={formik.handleBlur}
                disabled={formik.isSubmitting}
              />
            </Grid>

            <div className={styles.buttonsContainer}>
              <Button variant="secondary" onClick={() => setSchemaDialogOpen(true)}>
                {t("createEditMode.buttons.schema")}
              </Button>
              <Button variant="secondary" onClick={handleCancel}>
                {t("createEditMode.buttons.cancel")}
              </Button>
              <Button
                variant="primary"
                disabled={
                  isCreateWorkflowMutationLoading || isUpdateWorkflowMutationLoading || !formik.values.workflowName
                }
                onClick={formik.handleSubmit}
                isLoading={isCreateWorkflowMutationLoading || isUpdateWorkflowMutationLoading}
              >
                {mode === "create" ? t("createEditMode.buttons.create") : t("createEditMode.buttons.update")}
              </Button>
            </div>
          </div>
        )}
      </Grid>
    </Fragment>
  )
}
